import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Compass,
  ArrowRight,
  ShieldCheck,
  LogIn,
  Sparkles,
  ShoppingBag,
  Music,
  Library,
  BookOpen,
  Users,
  Heart,
  MessageCircle,
  MapPin,
  Star,
  CheckCircle2,
  Lock,
  HandHeart,
  Sun,
  Waves,
  Anchor,
  LifeBuoy,
  UserCheck,
  Phone,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import HelpNowButton from "@/components/HelpNowButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// TYVENA acronym pillars retired with the rebrand — new mission statement
// coming from the founder. See the mission-section for placeholder copy.

const trustChips = [
  { icon: Compass, label: "Guidance" },
  { icon: Sparkles, label: "Clarity" },
  { icon: HandHeart, label: "Trusted Care" },
  { icon: Lock, label: "HIPAA Aligned" },
];

const painPoints = [
  {
    icon: MessageCircle,
    title: "\u201CA case manager came in and said we need to get help at home\u2026 is that home care, home health, hospice, or what?\u201D",
  },
  {
    icon: Phone,
    title: "\u201CI've been on hold with three companies today and I'm no closer to help.\u201D",
  },
  {
    icon: Waves,
    title: "\u201CIs any of this covered by Medicare? Who's going to pay for it all?\u201D",
  },
];

const steps = [
  {
    step: "1",
    icon: MessageCircle,
    title: "Share your situation",
    description: "Tell Asher about your loved one, what you're seeing, what you've tried, and what worries you most. Plain conversation, no forms to fill out first.",
  },
  {
    step: "2",
    icon: Compass,
    title: "Get personalized guidance",
    description: "Asher explains your care options in plain English, including what's covered, who qualifies, and what to expect, then recommends the type of care that actually fits.",
  },
  {
    step: "3",
    icon: HandHeart,
    title: "Connect with local providers",
    description: "Browse vetted local providers, sign a Care Provider Choice Form for your doctor, and stay in touch through your private family portal.",
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleTalkToAsher = () => navigate("/chat");
  const handleGetStarted = () => navigate("/get-started");
  const handleBrowse = () => navigate("/browse-providers");

  return (
    <div className="min-h-screen bg-[#FAF9F6] font-body text-text-primary">
      {/* Navigation */}
      <nav
        data-testid="marketing-nav"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5"
      >
        <div className="flex items-center justify-between gap-4">
          <button
            type="button"
            data-testid="nav-home-brand"
            onClick={() => navigate("/get-started")}
            className="flex items-center flex-shrink-0 group cursor-pointer"
            aria-label="Explore care types"
          >
            <img
              src="/tyvena-lockup-horizontal.png"
              alt="Tyvena"
              className="h-12 md:h-14 w-auto rounded-xl shadow-md group-hover:shadow-lg transition-shadow"
            />
          </button>
          <div className="flex items-center gap-1.5 md:gap-3 flex-wrap justify-end">
            <Button
              data-testid="nav-browse-providers-btn"
              onClick={handleBrowse}
              variant="ghost"
              className="text-accent hover:bg-primary/5 font-body font-semibold text-sm md:text-base px-2 md:px-4"
            >
              Browse Providers
            </Button>
            <Button
              data-testid="nav-library-btn"
              onClick={() => navigate("/library")}
              variant="ghost"
              className="text-accent hover:bg-primary/5 font-body font-semibold text-sm md:text-base px-2 md:px-4"
            >
              <BookOpen className="w-4 h-4 mr-1 md:mr-2" />
              Library
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="nav-extras-btn"
                  variant="ghost"
                  className="relative text-accent hover:bg-primary/5 font-body font-semibold text-sm md:text-base px-2 md:px-4"
                >
                  <Sparkles className="w-4 h-4 mr-1 md:mr-2 text-secondary" />
                  Extras
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72 bg-white border border-[#E2E8F0] shadow-xl rounded-xl p-2">
                <DropdownMenuLabel className="font-body text-xs uppercase tracking-wider text-text-secondary px-2 pt-1 pb-2">
                  Curated for the care journey
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-shopping" onClick={() => navigate("/shopping")} className="cursor-pointer focus:bg-secondary/5 hover:bg-secondary/5 px-2 py-3 rounded-lg">
                  <div className="flex items-start gap-3 w-full">
                    <div className="mt-0.5 w-9 h-9 rounded-lg bg-secondary/15 flex items-center justify-center flex-shrink-0">
                      <ShoppingBag className="w-4 h-4 text-secondary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-body font-semibold text-sm text-text-primary">Recommended Shopping</span>
                      <p className="text-xs text-text-secondary font-body leading-snug mt-0.5">
                        Curated products for the care journey.
                      </p>
                    </div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-music" onClick={() => navigate("/music")} className="cursor-pointer focus:bg-primary/5 hover:bg-primary/5 px-2 py-3 rounded-lg">
                  <div className="flex items-start gap-3 w-full">
                    <div className="mt-0.5 w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
                      <Music className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-body font-semibold text-sm text-text-primary">Music for the Journey</span>
                      <p className="text-xs text-text-secondary font-body leading-snug mt-0.5">
                        Playlists for memory care and calm.
                      </p>
                    </div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-books" onClick={() => navigate("/books")} className="cursor-pointer focus:bg-accent/5 hover:bg-accent/5 px-2 py-3 rounded-lg">
                  <div className="flex items-start gap-3 w-full">
                    <div className="mt-0.5 w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Library className="w-4 h-4 text-accent" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-body font-semibold text-sm text-text-primary">Books &amp; Resources</span>
                      <p className="text-xs text-text-secondary font-body leading-snug mt-0.5">
                        Hand-picked books and free national resources.
                      </p>
                    </div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-inspiration" onClick={() => navigate("/inspiration")} className="cursor-pointer focus:bg-secondary/5 hover:bg-secondary/5 px-2 py-3 rounded-lg">
                  <div className="flex items-start gap-3 w-full">
                    <div className="mt-0.5 w-9 h-9 rounded-lg bg-secondary/15 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-secondary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-body font-semibold text-sm text-text-primary">Inspiration &amp; Education</span>
                        <span className="text-[10px] font-body font-bold uppercase tracking-wider bg-secondary/15 text-secondary px-1.5 py-0.5 rounded-full">
                          Coming soon
                        </span>
                      </div>
                      <p className="text-xs text-text-secondary font-body leading-snug mt-0.5">
                        Daily &amp; weekly videos, our podcast, and short lessons.
                      </p>
                    </div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {!user && (
              <Button
                data-testid="nav-signin-btn"
                onClick={() => navigate("/auth")}
                variant="outline"
                className="border-2 border-primary text-primary hover:bg-primary/5 rounded-full font-body font-bold text-sm md:text-base px-3 md:px-5"
              >
                <LogIn className="w-4 h-4 mr-1 md:mr-2" />
                Sign In
              </Button>
            )}
            <Button
              data-testid="nav-portal-btn"
              onClick={() => navigate("/portal")}
              className="bg-primary text-white hover:bg-primary-dark rounded-full font-body font-bold text-sm md:text-base px-3 md:px-5 shadow-md"
            >
              <ShieldCheck className="w-4 h-4 mr-1 md:mr-2" />
              My Portal
            </Button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden"
      >
        {/* Ocean-gradient backdrop, softened so the coastal-teal logo mark pops against it */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1F5A82] via-[#2A7A94] to-[#3FA79A]" aria-hidden="true"></div>
        {/* Lighthouse beam sweep */}
        <div className="absolute inset-0 opacity-40 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[radial-gradient(circle_at_center,rgba(224,123,84,0.35),transparent_60%)]"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,rgba(255,255,255,0.12),transparent_50%)]"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 md:pt-20 pb-24 md:pb-28">
          <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${isLoaded ? 'animate-fade-in' : 'opacity-0'}`}>
            {/* Left */}
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                <span className="w-2 h-2 rounded-full bg-secondary animate-pulse"></span>
                <span className="text-xs md:text-sm font-body font-bold uppercase tracking-[0.15em] text-white/95">
                  Your threshold to what comes next
                </span>
              </div>

              <h1 className="font-heading font-extrabold text-white leading-[1.05] tracking-tight text-4xl sm:text-5xl lg:text-6xl xl:text-7xl">
                Care coordination,
                <span className="block text-secondary">simplified.</span>
              </h1>

              <p className="text-lg md:text-xl leading-relaxed text-white/85 max-w-xl font-body">
                One app. Personalized guidance. Trusted care connections. Tyvena is your GPS for the care space, a warm and knowledgeable guide through home care, home health, palliative, hospice, and everything in between.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  data-testid="hero-talk-to-asher-btn"
                  onClick={handleTalkToAsher}
                  className="min-h-[52px] px-8 py-6 bg-secondary text-white hover:bg-[#C96540] rounded-full font-bold text-base md:text-lg shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all active:scale-95 font-body"
                >
                  Talk to Asher
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  data-testid="hero-browse-btn"
                  onClick={handleBrowse}
                  variant="outline"
                  className="min-h-[52px] px-8 py-6 border-2 border-white/40 text-white hover:bg-white/10 rounded-full font-bold text-base md:text-lg transition-all bg-transparent font-body"
                >
                  Search Providers
                </Button>
              </div>

              {/* Trust chips */}
              <div data-testid="hero-trust-chips" className="flex flex-wrap gap-2 md:gap-3 pt-3">
                {trustChips.map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    data-testid={`trust-chip-${label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="flex items-center gap-2 px-3.5 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/15"
                  >
                    <Icon className="w-3.5 h-3.5 text-seafoam" />
                    <span className="text-xs md:text-sm font-body font-semibold text-white/95">{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Lighthouse medallion. Cream disc gives the teal mark clean contrast. */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <div className="relative w-[300px] h-[300px] md:w-[380px] md:h-[380px]">
                {/* Beam glow */}
                <div className="absolute inset-0 rounded-full bg-secondary/30 blur-3xl animate-pulse" aria-hidden="true"></div>
                {/* Rings */}
                <div className="absolute inset-0 rounded-full border border-white/25" aria-hidden="true"></div>
                <div className="absolute inset-4 rounded-full border border-white/15" aria-hidden="true"></div>
                {/* Medallion — mark on a soft mist disc so the coastal-teal doorway pops */}
                <div className="absolute inset-8 rounded-full overflow-hidden shadow-2xl ring-4 ring-white/40 bg-[#F5F1E8] flex items-center justify-center p-6">
                  <img src="/tyvena-mark-512.png" alt="Tyvena doorway" className="w-full h-full object-contain" />
                </div>
                {/* Rotating dashed ring */}
                <div className="absolute -inset-2 rounded-full border-2 border-dashed border-secondary/50 animate-[spin_40s_linear_infinite]" aria-hidden="true"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom wave divider */}
        <div className="absolute bottom-0 left-0 right-0 leading-[0]" aria-hidden="true">
          <svg viewBox="0 0 1440 90" className="w-full h-[60px] md:h-[90px]" preserveAspectRatio="none">
            <path fill="#FAF9F6" d="M0,64 C240,96 480,32 720,48 C960,64 1200,96 1440,64 L1440,90 L0,90 Z" />
          </svg>
        </div>
      </section>

      {/* MEET ASHER — warm intro right under the hero */}
      <section
        data-testid="meet-asher-section"
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center">
          {/* Full-body Asher */}
          <div className="relative flex justify-center md:justify-start">
            <div className="absolute -inset-8 bg-gradient-to-br from-primary/15 via-secondary/10 to-transparent rounded-full blur-3xl" aria-hidden="true" />
            <img
              src="/asher-character-full.png?v=2"
              alt="Asher, your care guide"
              data-testid="meet-asher-portrait"
              className="relative w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 object-contain drop-shadow-2xl"
              onError={(e) => { e.currentTarget.src = "/asher-avatar.png"; }}
            />
          </div>

          {/* Copy + CTA */}
          <div>
            <p className="text-xs md:text-sm font-body font-bold uppercase tracking-[0.2em] text-secondary mb-3">
              Meet your guide
            </p>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-accent leading-[1.1] tracking-tight">
              Meet Asher.
            </h2>
            <div className="mt-6 space-y-4 text-lg md:text-xl text-text-secondary leading-relaxed font-body">
              <p>
                Life&apos;s transitions can leave you wondering, &ldquo;What do we do next?&rdquo; That&apos;s where Asher comes in.
              </p>
              <p>
                Asher is your guide within Tyvena — helping you make sense of what&apos;s ahead, discover resources that fit your unique needs, and navigate your next steps with greater clarity and confidence.
              </p>
              <p className="text-accent font-heading font-semibold italic">
                You don&apos;t have to figure it all out alone. Asher is here to help you find your way.
              </p>
            </div>
            <div className="mt-8">
              <Button
                data-testid="meet-asher-cta"
                onClick={() => navigate("/chat")}
                className="bg-primary hover:bg-primary-dark text-white rounded-full px-8 py-6 text-base md:text-lg font-body font-semibold shadow-lg hover:shadow-xl transition-all"
              >
                Chat with Asher
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM — Option 2: warmer / emotional. Meets families right where the ache lives. */}
      <section data-testid="problem-section" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-3xl">
          <p className="text-xs md:text-sm font-body font-bold uppercase tracking-[0.2em] text-secondary mb-3">You don&apos;t have to walk it alone</p>
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-accent leading-[1.1] tracking-tight">
            When life changes, everything can feel uncertain.
          </h2>
          <div className="mt-6 space-y-4 text-lg md:text-xl text-text-secondary leading-relaxed font-body">
            <p>
              Aging. A new diagnosis. Becoming a caregiver. A change in independence. A family member needing more help.
            </p>
            <p>
              These moments can leave families standing at the threshold of a new season — often overwhelmed by questions and unsure where to turn.
            </p>
            <p className="text-text-primary font-semibold">
              Tyvena is here to help you take the next step.
            </p>
            <p>
              We bring together the information, resources, services, and people you need to navigate what&apos;s next — with greater confidence and less fear.
            </p>
            <p className="text-accent font-heading font-semibold italic text-xl md:text-2xl pt-2">
              Because no one should have to stand at the threshold alone.
            </p>
          </div>
        </div>

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {painPoints.map(({ icon: Icon, title }, i) => (
            <div
              key={i}
              data-testid={`pain-point-${i + 1}`}
              className="bg-white border-l-4 border-secondary rounded-xl p-6 md:p-7 shadow-sm hover:shadow-md transition-shadow"
            >
              <Icon className="w-6 h-6 text-secondary mb-3" strokeWidth={2} />
              <p className="text-base md:text-lg font-body italic text-text-primary leading-snug">{title}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" data-testid="how-it-works-section" className="bg-mist py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs md:text-sm font-body font-bold uppercase tracking-[0.2em] text-primary mb-3">How Tyvena works</p>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-accent leading-[1.1] tracking-tight">
              Three steps. Real clarity. Zero pressure.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {steps.map(({ step, icon: Icon, title, description }, i) => (
              <div
                key={step}
                data-testid={`how-it-works-step-${step}`}
                className="relative bg-white rounded-2xl p-7 md:p-8 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1"
              >
                <div className="absolute -top-4 left-6 w-9 h-9 rounded-full bg-secondary text-white flex items-center justify-center font-heading font-extrabold text-lg shadow-md">
                  {step}
                </div>
                <Icon className="w-10 h-10 text-primary mt-4 mb-4" strokeWidth={1.75} />
                <h3 className="font-heading font-bold text-xl md:text-2xl text-accent mb-2.5">{title}</h3>
                <p className="text-base text-text-secondary font-body leading-relaxed">{description}</p>
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-primary/20" aria-hidden="true"></div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Button
              data-testid="how-it-works-cta"
              onClick={handleGetStarted}
              variant="outline"
              className="border-2 border-primary text-primary hover:bg-primary/5 rounded-full font-body font-bold px-8 py-6 min-h-[52px]"
            >
              See all care types we cover
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* MISSION — Tyvena's core promise, in Cheryl's voice */}
      <section data-testid="mission-section" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-3xl mb-10 md:mb-14">
          <p className="text-xs md:text-sm font-body font-bold uppercase tracking-[0.2em] text-primary mb-3">What Tyvena stands for</p>
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-accent leading-[1.05] tracking-tight">
            Every new chapter begins at a <span className="text-primary">threshold.</span>
          </h2>
          <div className="mt-6 space-y-4 text-lg md:text-xl text-text-secondary leading-relaxed font-body">
            <p>When life changes, knowing what to do next can feel overwhelming.</p>
            <p>
              Tyvena comes alongside individuals, families, and caregivers to help navigate what&apos;s next — with <span className="text-primary font-semibold">clarity</span>, <span className="text-secondary font-semibold">connection</span>, and <span className="text-accent font-semibold">confidence</span>.
            </p>
            <p className="text-text-primary font-semibold italic text-xl md:text-2xl">
              You don&apos;t have to know what&apos;s behind the door. You just need someone to help you open it.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {[
            {
              label: "Guidance",
              icon: Compass,
              body: "A knowledgeable voice in your corner — one that speaks plain English, meets you where you are, and adjusts as your journey does.",
              color: "text-primary",
              bg: "bg-primary/10",
            },
            {
              label: "Clarity",
              icon: Sparkles,
              body: "Payment sources explained. Eligibility demystified. Provider options shown side by side. Decisions made from a place of understanding, not fear.",
              color: "text-secondary",
              bg: "bg-secondary/15",
            },
            {
              label: "Trusted Care",
              icon: HandHeart,
              body: "Vetted local providers, chosen by you, not sold to you. Every family has the right to choose — and Tyvena was built to support that 100%.",
              color: "text-accent",
              bg: "bg-accent/10",
            },
          ].map(({ label, icon: Icon, body, color, bg }) => (
            <div
              key={label}
              data-testid={`mission-value-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className="bg-white rounded-2xl p-6 md:p-7 border border-[#E2E8F0] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
            >
              <div className={`w-12 h-12 rounded-full ${bg} flex items-center justify-center mb-4`}>
                <Icon className={`w-6 h-6 ${color}`} strokeWidth={2} />
              </div>
              <h3 className="font-heading font-bold text-xl md:text-2xl text-accent mb-3">
                {label}
              </h3>
              <p className="text-base text-text-secondary font-body leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* OUR STORY / FOUNDERS — Option 1 (Cheryl's favorite: The Threshold) leads
          the narrative, then the founders' clinical experience anchors it. */}
      <section data-testid="founders-section" className="bg-accent relative overflow-hidden py-16 md:py-24">
        {/* Decorative teal wash */}
        <div className="absolute inset-0 pointer-events-none opacity-30" aria-hidden="true">
          <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-primary blur-3xl"></div>
          <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-secondary blur-3xl"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs md:text-sm font-body font-bold uppercase tracking-[0.2em] text-secondary mb-4">Our story</p>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-white leading-[1.1] tracking-tight">
              Every new chapter begins at a threshold.
            </h2>
          </div>

          <div className="mt-8 md:mt-10 grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
            <div className="lg:col-span-3 space-y-5 text-lg md:text-xl text-white/85 font-body leading-relaxed">
              <p>
                There are moments in life when we find ourselves standing at a door we never expected to walk through — facing a new diagnosis, a change in independence, the responsibility of caring for someone we love, or simply wondering, <em className="text-white">What comes next?</em>
              </p>
              <p>
                The unknown can feel overwhelming. But you don&apos;t have to navigate it alone.
              </p>
              <p>
                Tyvena comes alongside individuals and families at the threshold of what&apos;s next — bringing <span className="text-secondary font-semibold">clarity</span> to the choices, <span className="text-secondary font-semibold">connections</span> to trusted resources, and <span className="text-secondary font-semibold">guidance</span> for the journey ahead.
              </p>
              <p>
                Because the next chapter doesn&apos;t have to be frightening simply because it is unfamiliar.
              </p>
              <p className="font-body font-semibold text-white text-xl md:text-2xl italic pt-2">
                You don&apos;t have to know what&apos;s behind the door. You just need someone to help you open it.
              </p>
              <p className="pt-4 text-base md:text-lg text-white/75">
                Tyvena was built by nurses with <span className="font-bold text-white">50+ years</span> of combined professional and personal caregiving experience — much of it inside home health, hospice, and palliative care. We&apos;ve walked this road alongside families through some of life&apos;s hardest chapters, and we built this app because we learned, one family at a time, the value of empowering people through education, information, and choice.
              </p>
            </div>

            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <UserCheck className="w-5 h-5 text-secondary" />
                  <p className="font-body font-bold text-white text-sm uppercase tracking-wider">Clinical experience</p>
                </div>
                <p className="text-white/85 font-body text-sm leading-relaxed">
                  Registered nursing, home health, hospice, palliative care, geriatric care management.
                </p>
              </div>
              <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Heart className="w-5 h-5 text-secondary" />
                  <p className="font-body font-bold text-white text-sm uppercase tracking-wider">Personal caregiving</p>
                </div>
                <p className="text-white/85 font-body text-sm leading-relaxed">
                  We&apos;ve sat where you sit, as daughters, spouses, and family caregivers navigating aging parents.
                </p>
              </div>
              <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Star className="w-5 h-5 text-secondary" />
                  <p className="font-body font-bold text-white text-sm uppercase tracking-wider">Why we built this</p>
                </div>
                <p className="text-white/85 font-body text-sm leading-relaxed">
                  Because no family should have to figure this out alone at 2am on Google.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DUAL PATH — Families | Providers */}
      <section data-testid="dual-audience-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-14">
          <p className="text-xs md:text-sm font-body font-bold uppercase tracking-[0.2em] text-primary mb-3">Two paths in</p>
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-accent leading-[1.1] tracking-tight">
            Whichever side of the care journey you're on, we've built a home for you.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {/* Families */}
          <div
            data-testid="path-families"
            className="group relative overflow-hidden bg-gradient-to-br from-primary to-primary-dark rounded-3xl p-8 md:p-10 text-white shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all"
          >
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-secondary/20 blur-3xl" aria-hidden="true"></div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full mb-6">
                <HandHeart className="w-3.5 h-3.5 text-secondary" />
                <span className="text-xs font-body font-bold uppercase tracking-wider">For families</span>
              </div>
              <h3 className="font-heading font-extrabold text-2xl md:text-3xl lg:text-4xl mb-4 leading-tight">
                Find the right care, with a guide who's been there.
              </h3>
              <ul className="space-y-2.5 mb-8 text-white/90 font-body">
                {["Conversational guidance from Asher, one question at a time", "Vetted local providers with transparent quality data", "Secure family portal with your signed Care Choice Form", "Free to use, always"].map((line) => (
                  <li key={line} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid="path-families-cta"
                onClick={handleTalkToAsher}
                className="min-h-[48px] px-7 py-5 bg-secondary text-white hover:bg-[#C96540] rounded-full font-bold font-body shadow-lg hover:shadow-xl transition-all"
              >
                Start with Asher
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Providers */}
          <div
            data-testid="path-providers"
            className="group relative overflow-hidden bg-white rounded-3xl p-8 md:p-10 border-2 border-accent/10 shadow-lg hover:shadow-2xl hover:-translate-y-1 hover:border-accent/30 transition-all"
          >
            <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-primary/10 blur-3xl" aria-hidden="true"></div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent/8 rounded-full mb-6">
                <Users className="w-3.5 h-3.5 text-accent" />
                <span className="text-xs font-body font-bold uppercase tracking-wider text-accent">For providers</span>
              </div>
              <h3 className="font-heading font-extrabold text-2xl md:text-3xl lg:text-4xl mb-4 leading-tight text-accent">
                When families need help, be the resource they find.
              </h3>
              <p className="text-text-secondary font-body mb-6">
                Tyvena helps families discover the services, care and community resources they need
                for an aging loved one — right when they need them.
              </p>
              <ul className="space-y-2.5 mb-8 text-text-secondary font-body">
                {[
                  "Put your services in front of families who are looking for help.",
                  "Families connect with you directly. They decide who they want to learn more about and when they’re ready to reach out.",
                  "Help families make informed choices. Share your expertise, your services and the difference you make.",
                  "Become part of the Tyvena community. Together, we’re making the journey of caring for an aging loved one a little easier to navigate.",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid="path-providers-cta"
                onClick={() => navigate("/provider-signup")}
                variant="outline"
                className="min-h-[48px] px-7 py-5 border-2 border-accent text-accent hover:bg-accent hover:text-white rounded-full font-bold font-body transition-all"
              >
                Request Provider Info
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA BAND — Option 3: brand-forward closer. */}
      <section data-testid="final-cta-section" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-dark to-accent" aria-hidden="true"></div>
        <div className="absolute inset-0 opacity-30 pointer-events-none" aria-hidden="true">
          <div className="absolute top-0 right-1/4 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(224,123,84,0.4),transparent_60%)]"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
          <Compass className="w-14 h-14 text-secondary mx-auto mb-6" strokeWidth={1.5} />
          <p className="text-xs md:text-sm font-body font-bold uppercase tracking-[0.2em] text-secondary/90 mb-4">What&apos;s next starts here</p>
          <h2 className="font-heading font-extrabold text-white text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight">
            Life doesn&apos;t come with a roadmap.
          </h2>
          <div className="mt-6 space-y-4 text-lg md:text-xl text-white/85 font-body max-w-2xl mx-auto leading-relaxed">
            <p>
              Especially when we reach a new season of life, the path ahead can feel unfamiliar. There may be decisions to make, resources to find, questions you don&apos;t know how to ask.
            </p>
            <p className="text-white font-semibold">
              Tyvena helps you find your way.
            </p>
            <p>
              At the threshold of what&apos;s next, we connect individuals, families, and caregivers with the guidance, resources, and possibilities that can help turn uncertainty into a path forward.
            </p>
            <p className="text-white font-heading font-semibold italic pt-2">
              Standing at the threshold doesn&apos;t mean you&apos;re lost. It means you&apos;re about to enter something new.
            </p>
          </div>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button
              data-testid="final-cta-talk-to-asher"
              onClick={handleTalkToAsher}
              className="min-h-[52px] px-8 py-6 bg-secondary text-white hover:bg-[#C96540] rounded-full font-bold text-base md:text-lg shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all font-body"
            >
              Talk to Asher (it&apos;s free)
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              data-testid="final-cta-portal"
              onClick={() => navigate("/portal")}
              variant="outline"
              className="min-h-[52px] px-8 py-6 border-2 border-white/40 text-white hover:bg-white/10 rounded-full font-bold text-base md:text-lg bg-transparent font-body transition-all"
            >
              <ShieldCheck className="mr-2 w-5 h-5" />
              Open My Portal
            </Button>
          </div>
          <p className="mt-8 text-sm font-body text-white/70 italic">
            Tyvena. Helping you navigate what&apos;s next.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-[#E2E8F0] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/tyvena-lockup-horizontal.png" alt="Tyvena" className="h-10 w-auto rounded-lg shadow-md" />
              <span className="font-body text-sm text-text-secondary hidden sm:inline">
                Your threshold to what comes next.
              </span>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
              <Button
                data-testid="become-provider-btn"
                onClick={() => navigate("/provider-signup")}
                className="px-6 py-2 bg-secondary text-white hover:bg-[#C96540] rounded-full font-bold transition-all duration-300 font-body"
              >
                Request Provider Info
              </Button>
              <div className="flex items-center gap-4 text-sm font-body">
                <button
                  data-testid="footer-terms-link"
                  onClick={() => navigate("/legal/terms")}
                  className="text-text-secondary hover:text-primary transition-colors"
                >
                  Terms
                </button>
                <span className="text-[#E2E8F0]">·</span>
                <button
                  data-testid="footer-privacy-link"
                  onClick={() => navigate("/legal/privacy")}
                  className="text-text-secondary hover:text-primary transition-colors"
                >
                  Privacy
                </button>
                <span className="text-[#E2E8F0]">·</span>
                <button
                  data-testid="footer-hipaa-link"
                  onClick={() => navigate("/legal/hipaa-notice")}
                  className="text-text-secondary hover:text-primary transition-colors"
                >
                  HIPAA Notice
                </button>
              </div>
            </div>
          </div>
          <p className="text-xs text-text-secondary font-body text-center md:text-right mt-6">
            © 2026 Tyvena. A guide for the care journey.
          </p>
        </div>
      </footer>

      {/* Always-visible "Need help now?" pill for anyone overwhelmed */}
      <HelpNowButton />
    </div>
  );
}
